<script lang="ts">
  import { onMount } from 'svelte';
  import VideoPlayer from './VideoPlayer.svelte';
  import IndustryTag from './IndustryTag.svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';

  export let title: string;
  export let client: string;
  export let industry: string;
  export let product: string;
  export let heroMedia: {
    type: 'video' | 'image';
    src: string;
    poster?: string;
  };
  export let metrics: Array<{
    value: number;
    label: string;
  }>;
  export let roles: string[];
  export let clientLogo: string = "";
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let numberElement: HTMLElement;
  let currentValues = metrics.map(() => 0);

  onMount(() => {
    setupProjectAnimations();
    metrics.forEach((metric, i) => {
      animateValue(
        0,
        metric.value,
        1000,
        (value) => {
          currentValues[i] = value;
        }
      );
    });
  });

  function formatNumber(num: number): string {
    return num.toLocaleString();
  }
</script>

<svelte:window bind:scrollY />

<!-- Hero Section -->
<section class="relative overflow-hidden bg-gray-50">
  <!-- Large Title with parallax -->
  <div class="container px-8 md:px-16 pt-24">
    <h1 
      class="mix-blend-multiply will-change-transform animate-on-view opacity-100"
      style="transform: translateY({translateY}px); color: var(--accent);"
      data-delay="0"
    >
      {title}
    </h1>
  </div>

  <!-- Key Visual with Parallax -->
  <div class="w-full mt-8 animate-on-view opacity-100" data-delay="5">
    <div class="aspect-video w-full overflow-hidden">
      {#if heroMedia.type === 'video'}
        <VideoPlayer
          src={heroMedia.src}
          poster={heroMedia.poster}
          autoplay={true}
          loop={true}
          controls={false}
          muted={true}
        />
      {:else}
        <img 
          src={heroMedia.src}
          alt={title}
          class="w-full h-auto object-cover"
        />
      {/if}
    </div>
  </div>

 <!-- Content Grid -->
 <div class="content-grid container mx-auto px-4 sm:px-8 md:px-16 h-full pt-12 sm:pt-24">
  <div class="grid grid-cols-1 items-start gap-1 lg:gap-16">
    <!-- Left Content -->
    <div class="max-w-2xl">
      <!-- Case Study Header -->
      <div class="space-y-2 mb-6 border-t border-b border-black/10 py-4 sm:py-6 animate-on-view opacity-100" data-delay="1">
        <div class="grid grid-cols-2 gap-4 sm:gap-16">
          {#if client}
            <div>
              <h3 class="section-label">Client</h3>
              <div class="flex flex-col items-start gap-1">
                {#if clientLogo}
                  <img src={clientLogo} alt="Client Logo" style="height: 4rem; width: auto; margin-bottom: 0.25rem;" />
                {/if}
                <span class="text-xs text-black/90 font-light" style="line-height: 2;">
                  {client}
                </span>
              </div>
            </div>
          {/if}
          {#if industry}
            <div>
              <h3 class="section-label">Industry</h3>
              <p class="text-lg text-black/90 font-light" style="line-height: 2;">
                <IndustryTag tag={industry} />
              </p>
            </div>
          {/if}
        </div>
      </div>

      {#if product}
        <div class="space-y-2 mb-8 animate-on-view opacity-100" data-delay="1">
          <h3 class="section-label">Product</h3>
          <span class="text-lg text-black/90 font-light leading-relaxed max-w-xl">
            {product}
          </span>
        </div>
      {/if}
      
      <!-- Metrics -->
      <div class="space-y-2 mb-6 animate-on-view opacity-100" data-delay="2">
        <h3 class="section-label">In Numbers</h3>
        <div class="metrics-section">
          {#each metrics as metric, i}
            <div>
              <span class="text-2xl sm:text-3xl md:text-4xl font-light">
                {formatNumber(currentValues[i])}
              </span>
              <span class="text-small">{metric.label}</span>
            </div>
          {/each}
        </div>
      </div>

      <!-- My Role -->
      <div class="mb-1 animate-on-view opacity-100" data-delay="3">
        <h3 class="section-label">My Role</h3>
      </div>

      <!-- Navigation Pills -->
      <div class="nav-pills animate-on-view opacity-100" data-delay="4">
        {#if roles}
          <p class="text-base text-black/80 leading-relaxed">{@html roles}</p>
        {/if}
      </div>
      <slot name="award" />
    </div>
</section>

<style>
  section {
    padding-bottom: 60px;
    position: relative;
    z-index: 2;
    background: var(--bg-color, #f9fafb);
  }

  @media (min-width: 768px) {
    section {
      padding-bottom: 100px;
    }
  }

  h1 {
    transform-origin: left center;
    will-change: transform;
    font-family: 'Simple-Regular', monospace;
    @apply text-4xl sm:text-5xl md:text-6xl xl:text-7xl;
    line-height: 1;
    letter-spacing: -0.01em;
    max-width: 20ch;
    font-weight: 300;
    color: var(--accent);
  }

  .content-grid {
    position: relative;
    z-index: 1;
    background: var(--bg-color, #f9fafb);
  }

  :global(.section-label) {
    @apply text-sm uppercase tracking-wider;
    color: var(--accent);
  }
</style> 